<script setup>
import { ref, computed, watch } from 'vue'
import Icon from '@/components/shell/Icon.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'
import { BILLING_STAGE_LIST } from '@/data/mockData.js'
import { generateInvoicePDF } from '@/utils/downloads.js'

const PRE = ['Order Confirmed','Invoice Pending','Invoice Generated','Payment Pending','Payment Reported']
const TONE = { 'Order Confirmed':'yellow','Invoice Pending':'orange','Invoice Generated':'yellow','Payment Pending':'orange','Payment Reported':'yellow','Payment Received':'green' }
const stageFilter = ref('All'); const search = ref(''); const drawerOrderNo = ref(null)
const downloading = ref(false)
const paymentReference = ref(''); const paymentMethod = ref('Bank Transfer')

const isPharmacyRole = computed(() => store.state.role === 'pharmacy')
// BUG FIX: this previously called getInvoices(scenario) directly for every
// non-pharmacy role, bypassing store.data.invoices (the version merged with
// billingOverrides). That meant "Mark Payment Received" — wired below —
// would silently not show up for Admin/Sales Supervisor, the exact roles
// meant to use it, even though the pharmacy-side view already worked
// correctly via store.data.myInvoices.
const invoices = computed(() => isPharmacyRole.value ? store.data.myInvoices.value : store.data.invoices.value)
const drawerInvoice = computed(() => drawerOrderNo.value ? invoices.value.find(i => i.orderNo === drawerOrderNo.value) || null : null)

// Phase 10: land here from a notification deep-link (relatedEntityId is an
// orderNo for Invoice-type notifications, since invoices are keyed by order
// in this data model). Watcher, not onMounted, for the same reason as every
// other pendingFocus consumer — AppShell won't remount this component if the
// user clicks a second notification while already on Invoice & Billing.
watch(() => store.state.pendingFocus, f => {
  if (f && f.type === 'invoice') {
    const match = invoices.value.find(i => i.orderNo === f.id)
    if (match) drawerOrderNo.value = match.orderNo
    store.clearPendingFocus()
  }
}, { immediate: true })
const drawerPharmacy = computed(() => drawerInvoice.value ? store.data.pharmacies.value.find(p => p.customerId === drawerInvoice.value.customerId) : null)
const drawerStageIdx = computed(() => drawerInvoice.value ? BILLING_STAGE_LIST.indexOf(drawerInvoice.value.billingStage) : -1)

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return invoices.value.filter(inv => (stageFilter.value==='All'||inv.billingStage===stageFilter.value) && (!q||(inv.invoiceNo&&inv.invoiceNo.toLowerCase().includes(q))||inv.orderNo.toLowerCase().includes(q)||inv.pharmacyName.toLowerCase().includes(q)))
})
const pipeline = computed(() => { const c={}; BILLING_STAGE_LIST.forEach(s=>c[s]=0); invoices.value.forEach(i=>c[i.billingStage]++); return BILLING_STAGE_LIST.map(s=>({stage:s,count:c[s]})) })
const totalOutstanding = computed(() => invoices.value.filter(i=>PRE.includes(i.billingStage)).reduce((s,i)=>s+i.invoiceValue,0))
const totalSettled     = computed(() => invoices.value.filter(i=>i.billingStage==='Payment Received').reduce((s,i)=>s+i.invoiceValue,0))
const totalInvoiceValue= computed(() => invoices.value.reduce((s,i)=>s+i.invoiceValue,0))
const labels = computed(() => isPharmacyRole.value ? { outstanding:'Amount Payable (incl. pending confirmation)', settled:'Payment Confirmed' } : { outstanding:'Outstanding Receivable', settled:'Payment Received' })

const openDrawer = inv => { drawerOrderNo.value = inv.orderNo; paymentReference.value = ''; paymentMethod.value = 'Bank Transfer' }

// B1: split payment-status action. Pharmacy self-reports (satisfies BRD
// §4.1's "Update payment status" literally); Finance/Admin independently
// confirms (keeps the authoritative act with Finance, not the payer). Finance
// can confirm from EITHER Payment Pending or Payment Reported — a bank
// reconciliation is valid proof even if the pharmacy never self-reported.
const canReportPayment = computed(() => drawerInvoice.value && isPharmacyRole.value && drawerInvoice.value.billingStage === 'Payment Pending')
const canConfirmPayment = computed(() => drawerInvoice.value && !isPharmacyRole.value && !!drawerInvoice.value.invoiceNo && ['Payment Pending','Payment Reported'].includes(drawerInvoice.value.billingStage))
const reportPayment = () => { if (drawerOrderNo.value) store.reportPaymentMade(drawerOrderNo.value, paymentReference.value.trim(), paymentMethod.value) }
const confirmPayment = () => { if (drawerOrderNo.value) store.markPaymentReceived(drawerOrderNo.value) }

// Download with a brief simulated progress state, per the blueprint's ask
// for a loading indicator rather than an instant silent download.
async function downloadInvoice(inv) {
  downloading.value = true
  await new Promise(r => setTimeout(r, 600))
  const ph = store.data.pharmacies.value.find(p => p.customerId === inv.customerId)
  generateInvoicePDF(inv, ph)
  downloading.value = false
}
</script>
<template>
  <div>
    <div class="card module-header">
      <h2>{{ invoices.length }} {{ isPharmacyRole ? 'invoices for '+store.activePharmacy.value?.pharmacyName : 'orders in the billing pipeline' }}</h2>
    </div>
    <div v-if="!invoices.length" class="card empty-state">
      <div class="icon-wrap"><Icon name="file" :size="26" /></div>
      <h3>No invoices in this scenario</h3>
    </div>
    <template v-else>
      <div class="stat-row">
        <div class="card stat-card"><div class="stat-value">${{ Math.round(totalOutstanding).toLocaleString() }}</div><div class="stat-label">{{ labels.outstanding }}</div></div>
        <div class="card stat-card"><div class="stat-value" style="color:var(--status-green);">${{ Math.round(totalSettled).toLocaleString() }}</div><div class="stat-label">{{ labels.settled }}</div></div>
        <div class="card stat-card"><div class="stat-value">${{ Math.round(totalInvoiceValue).toLocaleString() }}</div><div class="stat-label">Total Invoice Value</div></div>
        <div class="card stat-card"><div class="stat-value">{{ invoices.filter(i=>i.invoiceNo).length }}</div><div class="stat-label">Invoices generated</div></div>
      </div>
      <div class="card" style="padding:16px 20px;margin-bottom:16px;">
        <div class="eyebrow" style="margin-bottom:12px;">Billing Pipeline</div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <div v-for="p in pipeline" :key="p.stage" style="flex:1;min-width:120px;">
            <div class="card" style="padding:12px 14px;box-shadow:none;text-align:center;">
              <div style="font-family:var(--font-display);font-size:22px;color:var(--color-primary-dark);">{{ p.count }}</div>
              <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px;">{{ p.stage }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="toolbar">
          <input class="search-input" v-model="search" placeholder="Search invoice no., order no. or pharmacy…" />
          <select class="filter-select" v-model="stageFilter"><option>All</option><option v-for="s in BILLING_STAGE_LIST" :key="s">{{ s }}</option></select>
          <span class="result-count">{{ filtered.length }} of {{ invoices.length }} shown</span>
        </div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Invoice No.</th><th>Order No.</th><th>Pharmacy</th><th>Invoice Date</th><th>Base (USD)</th><th>VAT 15%</th><th>Invoice Value</th><th>Billing Stage</th></tr></thead>
            <tbody>
              <tr v-for="inv in filtered" :key="inv.orderNo" class="clickable" @click="openDrawer(inv)">
                <td class="cell-strong">{{ inv.invoiceNo||'—' }}</td><td class="cell-muted">{{ inv.orderNo }}</td><td>{{ inv.pharmacyName }}</td>
                <td class="cell-muted">{{ inv.invoiceDate||'—' }}</td><td class="cell-muted">${{ inv.baseValue.toLocaleString() }}</td>
                <td class="cell-muted">${{ inv.taxAmount.toLocaleString() }}</td><td class="cell-strong">${{ inv.invoiceValue.toLocaleString() }}</td>
                <td><span class="status-pill" :class="TONE[inv.billingStage]||'yellow'"><span class="dot"></span>{{ inv.billingStage }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <DetailDrawer :open="!!drawerInvoice" :title="drawerInvoice?.invoiceNo || drawerInvoice?.orderNo || ''" @close="drawerOrderNo=null">
      <template v-if="drawerInvoice">
        <div class="oos-meta-grid" style="margin-bottom:18px;">
          <div><div class="label">Order No.</div>{{ drawerInvoice.orderNo }}</div>
          <div><div class="label">Pharmacy</div>{{ drawerInvoice.pharmacyName }}</div>
          <div><div class="label">Product</div>{{ drawerInvoice.productName }}</div>
          <div><div class="label">Invoice Date</div>{{ drawerInvoice.invoiceDate || 'Not yet generated' }}</div>
        </div>

        <div class="eyebrow" style="margin-bottom:10px;">Billing Pipeline</div>
        <div class="stepper" style="margin-bottom:18px;">
          <div v-for="(s,i) in BILLING_STAGE_LIST" :key="s" class="stepper-step">
            <div class="stepper-marker">
              <div class="stepper-dot" :class="{ done: i<drawerStageIdx, current: i===drawerStageIdx }"></div>
              <div v-if="i<BILLING_STAGE_LIST.length-1" class="stepper-line" :class="{ done: i<drawerStageIdx }"></div>
            </div>
            <div class="stepper-label" :class="i===drawerStageIdx?'current':(i<drawerStageIdx?'':'pending')">{{ s }}</div>
          </div>
        </div>

        <div class="eyebrow" style="margin-bottom:10px;">Tax Invoice Breakdown</div>
        <table class="data-table" style="margin-bottom:18px;">
          <thead><tr><th>Description</th><th style="text-align:right;">Amount (USD)</th></tr></thead>
          <tbody>
            <tr><td>{{ drawerInvoice.productName }} — Base Value</td><td class="cell-muted" style="text-align:right;">${{ drawerInvoice.baseValue.toLocaleString() }}</td></tr>
            <tr><td>Zimbabwe VAT (15%)</td><td class="cell-muted" style="text-align:right;">${{ drawerInvoice.taxAmount.toLocaleString() }}</td></tr>
            <tr><td class="cell-strong">Total Invoice Value</td><td class="cell-strong" style="text-align:right;">${{ drawerInvoice.invoiceValue.toLocaleString() }}</td></tr>
          </tbody>
        </table>

        <div style="display:flex; gap:10px; flex-wrap:wrap; align-items:flex-start;">
          <button v-if="drawerInvoice.invoiceNo" class="filter-select" style="cursor:pointer;" :disabled="downloading" @click="downloadInvoice(drawerInvoice)">
            {{ downloading ? 'Generating PDF…' : 'Download PDF Invoice' }}
          </button>
          <span v-else class="muted" style="font-size:12.5px;">No invoice generated yet — order hasn't reached the Invoice Generated stage.</span>
        </div>

        <div v-if="drawerInvoice.reportedReference || drawerInvoice.reportedMethod" class="oos-remarks" style="margin-top:14px;">
          <strong style="display:block;font-size:11px;text-transform:uppercase;letter-spacing:.04em;margin-bottom:3px;color:var(--color-text-muted);">Payment Reported by Pharmacy</strong>
          {{ drawerInvoice.reportedMethod }}{{ drawerInvoice.reportedReference ? ' — Ref: '+drawerInvoice.reportedReference : '' }}{{ drawerInvoice.reportedDate ? ' on '+drawerInvoice.reportedDate : '' }}
        </div>

        <!-- Pharmacy: report payment made -->
        <div v-if="canReportPayment" style="border-top:1px solid var(--color-border);margin-top:16px;padding-top:16px;">
          <div class="eyebrow" style="margin-bottom:10px;">Report Payment Made</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
            <div class="form-group" style="margin:0;">
              <label>Payment Method</label>
              <select class="form-select" v-model="paymentMethod">
                <option>Bank Transfer</option><option>Mobile Money</option><option>Cash</option><option>Cheque</option>
              </select>
            </div>
            <div class="form-group" style="margin:0;">
              <label>Reference No.</label>
              <input class="form-input" v-model="paymentReference" placeholder="e.g. TXN-88213" />
            </div>
          </div>
          <button class="btn-primary" @click="reportPayment">Mark Payment Made</button>
          <p class="muted" style="font-size:11.5px;margin-top:8px;">This reports the payment to Finance for confirmation — it doesn't settle the invoice by itself.</p>
        </div>
        <p v-else-if="isPharmacyRole && drawerInvoice.billingStage === 'Payment Reported'" class="muted" style="font-size:12.5px;margin-top:14px;">
          ✓ Payment reported — awaiting Finance confirmation.
        </p>

        <!-- Finance/Admin: confirm receipt -->
        <div v-if="canConfirmPayment" style="border-top:1px solid var(--color-border);margin-top:16px;padding-top:16px;">
          <div class="eyebrow" style="margin-bottom:10px;">Accounts Receivable</div>
          <button class="btn-primary" @click="confirmPayment">Confirm Payment Received</button>
        </div>
        <p v-if="!isPharmacyRole && drawerInvoice.billingStage==='Payment Received'" class="muted" style="font-size:12px;margin-top:10px;">✓ Payment already received for this invoice.</p>
      </template>
    </DetailDrawer>
  </div>
</template>